import Auth from '@aws-amplify/auth';
import Analytics from '@aws-amplify/analytics';
import { camelCase, isEmpty } from 'lodash';

/**
 * Convert the given dictionary object to an acceptable dictionary for use as `attributes` or `userAttributes` in
 * AWS Pinpoint requests.
 *
 * Converts the key to camel case and puts the value into a list.
 *
 * @param attributeObj - The json dictionary that needs to be converted.
 * @returns {{}}
 */
function objectToAttributeMapping(attributeObj) {
    let attributes = {};
    for (let [key, value] of Object.entries(attributeObj)) {
        // attribute values have to be stored inside of an array
        if (!Array.isArray(value)) value = [value];
        attributes[camelCase(key)] = value;
    }
    return attributes;
}

/**
 * Pinpoint only allows for event attributes to be a maximum of 200 characters long. In the case of a long URL,
 * we could exceed that.
 *
 * Go through the attributes dictionary and truncate the string if necessary.
 *
 * @param attributes - The json dictionary that needs to be converted.
 * @returns {attributes}
 */
function checkAttributesLength(attributes) {
    for (let key in attributes) {
        if (typeof attributes[key] === "string") {
            let attributeLength = attributes[key].length;
            if (attributeLength > 200) {
                    attributes[key] = `${attributes[key].slice(0, 197)}...`
            }
        }
    }
    return attributes
}

/**
 * Convert a list of products into a long string of product names.
 *
 * @param productsList
 * @returns {*}
 */
function getProductsNames(productsList) {
    return productsList.flatMap(product => product.name).join()
}

/**
 * Analytics provider for AWS Pinpoint
 *
 * Uses an unauthenticated AWS Cognito Identity Pool to track users.
 *
 * @param config - dictionary containing the connection information for cognito and pinpoint.
 * @param config.appId: The AWS Pinpoint app id.
 * @param config.region: The AWS region that pinpoint and cognito are located in.
 * @param config.identityPoolId: The ID of the identity pool that should be used for cognito.
 * @param config.pageMeta: Information about the page events are being fired on. This data is sent for all `track()` events.
 */
export default function amazonPinpoint(config) {
    return {
        NAMESPACE: 'amazon-pinpoint',
        config: config,
        initialize: ({config, instance}) => {
            // Configure Cognito
            Auth.configure({
                Auth: {
                    identityPoolId: config.identityPoolId,
                    region: config.region,
                }
            });

            // Configure Pinpoint
            Analytics.configure({
                AWSPinpoint: {
                    appId: config.appId,
                    region: config.region,
                    mandatorySignIn: false,
                    endpoint: {
                        channelType: 'EMAIL',
                    },
                }
            });

            if (instance.getState('context.debug') === true) {
                window.LOG_LEVEL = 'DEBUG';
            }
        },
        identify: ({ payload }) => {
            let properties = { userId: payload.userId };
            const identifyingTraits = JSON.parse(JSON.stringify(payload.traits));
            const user_data = identifyingTraits['user_payload'];
            // Remove user specific targeting from dict that will be converted to generic attributes
            delete identifyingTraits['user_payload'];

            if (!isEmpty(identifyingTraits)) {
                properties['attributes'] = objectToAttributeMapping(identifyingTraits);
            }

            // Only send this extra data if they are logged in.
            if (!isEmpty(user_data)) {
                properties['address'] = user_data.email;
                properties['userAttributes'] = objectToAttributeMapping(user_data);
            }
            Analytics.updateEndpoint(properties);
        },
        page: ({payload}) => {
            const {properties} = payload;
            let metrics = {};

            const attributes = checkAttributesLength(forceStringValues(properties));

            // Add views remaining value to the metrics
            if (Object.prototype.hasOwnProperty.call(attributes, 'views_remaining')) {
                metrics.views_remaining = parseInt(attributes.views_remaining, 10);
                delete attributes.views_remaining;
            }

            Analytics.record({
                name: 'PageView',
                attributes: attributes,
                metrics: metrics,
            });
        },
        track: ({payload}) => {
            let {properties, event} = payload;

            const attributes = checkAttributesLength(forceStringValues(properties));
            let metrics = {};

            // Some events needs special handling in order to map their event properties to
            // either attributes (whose values must be strings) or metrics (whose values must be numbers)
            switch (event) {
                case 'Purchase':
                    // Send special monetization event.
                    event = '_monetization.purchase';
                    attributes["_currency"] = "USD";
                    attributes["_product_id"] =  getProductsNames(properties.products);
                    attributes["_transaction_id"] = properties.order_id;
                    attributes["_item_price_formatted"] = `${forceNumberValue(properties.total)}`;
                    metrics["_quantity"] = 1.0;
                    metrics["_item_price"] = forceNumberValue(properties.total);
                    break;
                case 'AddPaymentInfo':
                case 'ViewProducts':
                    if (Object.prototype.hasOwnProperty.call(properties, 'total')) {
                        metrics.total = forceNumberValue(properties.total);
                        delete attributes.total;
                    }
                    if (Object.prototype.hasOwnProperty.call(properties, 'products')) {
                        attributes.products = getProductsNames(properties.products)
                    }
                    break;

                case 'AddToCart':
                    if (Object.prototype.hasOwnProperty.call(properties, 'product')) {
                        attributes.product = properties.product.name;
                        metrics.price = forceNumberValue(properties.product.price);
                    }
                    break;

                case 'DisplayReadPrompt':
                case 'DisplaySubscribePrompt':
                case 'RevealArticle':
                    if (Object.prototype.hasOwnProperty.call(properties, 'views_remaining')) {
                        metrics.views_remaining = forceNumberValue(properties.views_remaining);
                        delete attributes.views_remaining;
                    }
                    break;

                case 'FormSubmitInvalid':
                    if (Object.prototype.hasOwnProperty.call(properties, 'category')
                        && properties.category === 'Auth'){
                        event = '_userauth.auth_fail';
                    }
                    else if (Object.prototype.hasOwnProperty.call(properties, 'errors')) {
                        const error_dict = properties.errors;
                        let error_messages_string = "";
                        let error_count = 0;
                        // Turn the dictionary of errors into a comma separated string of the error messages grouped by the field
                        // name where different field error messages are separated by a semicolon. This is then used for the label.
                        // Also total the number of errors to use as the value for the event.
                        for (let [field_name, error_list] of Object.entries(error_dict)) {
                            // Separate different field messages with a semicolon.
                            if (error_messages_string !== "") {
                                error_messages_string += ";"
                            }
                            error_messages_string += `${field_name}:`;
                            error_messages_string += error_list.join();
                            error_count += error_list.length;
                        }

                        attributes.errors = error_messages_string;
                        metrics.error_count = error_count;
                    }
                    break;

                case 'LoadedContentView':
                    if (Object.prototype.hasOwnProperty.call(properties, 'loaded_content_number')) {
                        metrics.loaded_content_number = forceNumberValue(properties.loaded_content_number);
                        delete attributes.loaded_content_number;
                    }
                    break;

                case 'PrintSubscriberRequest':
                    if (!Object.prototype.hasOwnProperty.call(properties, 'is_print_subscriber')) {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    if (properties.is_print_subscriber !== 'on') {
                        // Don't send the event if they didn't check the print subscriber checkbox
                        return;
                    }
                    break;

                case 'Search':
                    if (Object.prototype.hasOwnProperty.call(properties, 'results_count')) {
                        metrics.results_count = forceNumberValue(properties.results_count);
                        delete attributes.results_count;
                    }
                    break;

                case 'SecondStreetSignup':
                case 'MailchimpSignup': {
                    let emailSignupNames = "";
                    for (let [signupName, value] of Object.entries(properties)) {
                        if (value === 'on') {
                            if (emailSignupNames !== '') {
                                emailSignupNames += ',';
                            }
                            emailSignupNames += signupName;
                            // Don't send field data to Pinpoint
                            delete attributes[signupName];
                        }
                    }
                    attributes['newsletters'] = emailSignupNames;
                    break;
                }
                case 'CompleteLogin':
                    event = '_userauth.sign_in';
                    break;
                case 'CompleteRegister':
                    event = '_userauth.sign_up';
                    break;
            }

            Analytics.record({
                name: event,
                attributes: attributes,
                metrics: metrics,
            });
        },
        loaded: () => {
            return Analytics.isAnalyticsConfigured();
        },
    }
}

/**
 * Convert all values in the JSON object to be strings.
 *
 * For pinpoint all attributes can only have string values.
 *
 * @param jsonObject - The JSON object whose values should be converted.
 * @returns {{}} - A new JSON object with all string values.
 */
function forceStringValues(jsonObject) {
    let obj = {};
    // Everything else gets treated as an attribute, which must be a string.
    for (let [key, value] of Object.entries(jsonObject)) {
        if (Array.isArray(value)) {
            value = value.join(",")
        } else {
            value = `${value}`;
        }
        obj[key] = value;
    }
    return obj;
}

/**
 * Convert the given value into a number.
 *
 * If the value cannot be converted, then we return 0.
 *
 * @param value - The value to convert.
 * @returns {number}
 */
function forceNumberValue(value) {
    if (typeof (value) === 'number') {
        return value;
    }
    const integerValue = parseInt(value, 10);
    if (Number.isNaN(integerValue)) {
        console.error(`Unable to convert ${value} to an integer.`);
        return 0;
    }
    return integerValue;
}
